import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { notify } from '../../context/FeedbackContext';
import './AdminPage.css';
import { FaSearch, FaFilter, FaEdit } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';

import {
  getFilteredNavItemsWithIcons,
  handleNavClick as routesHandleNavClick,
  hasPermission,
  getPageNameByPath
} from '../../config/routes';
import { useLocation } from 'react-router-dom';
import HasPermi from '../../components/HasPermi';
import { useRestaurant } from '../../context/RestaurantContext';

function RefId({ value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return <div className="user-id">ID: -</div>;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="user-id ref-id-wrapper" onClick={handleCopy}>
      <span className="ref-id-short">ID: {value.slice(0, 8)}...</span>
      <span className="ref-id-tooltip">{copied ? 'Copied!' : value}</span>
    </div>
  );
}



const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const sanitizePhone = (value) => {
  if (!value) return value;
  const hasPlus = value[0] === '+';
  const rest = hasPlus ? value.slice(1) : value;
  return (hasPlus ? '+' : '') + rest.replace(/[^\d\s-]/g, '');
};

const PAGE_SIZE = 6;

// ，每次调用时实时读取 localStorage
function getRoleOptions() {
  return localStorage.getItem("role") === "Owner"
    ? ['Accountant', 'Manager', 'Cashier', 'Service Crew']
    : ['POSYB Admin', 'Owner', 'Accountant', 'Manager', 'Cashier', 'Service Crew'];
}


function getEmptyForm(roleOptions) {
  return {
    id: null,
    reference: '',
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    password: '',
    role: roleOptions[0] || '',
    status: 'Active'
  };
}

function AdminPage({ onNavigate }) {

  const location = useLocation();
  const currentPageName = getPageNameByPath(location.pathname) || 'User Management';

  //  每次组件渲染时实时获取角色选项 如果需要更强的实时性，去掉 useMemo，直接用 getRoleOptions()
  const roleOptions = useMemo(() => getRoleOptions(), []); // 首次渲染
  

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 6,
    totalCount: 0
  });

  const handleNavClick = (item) => {
    if (!hasPermission(item)) {
      console.warn(`No permission to access: ${item}`);
      return;
    }
    routesHandleNavClick(item, onNavigate);
  };

  const { selectedRestaurant } = useRestaurant() || {};
  const [adminSection, setAdminSection] = useState('UserManagement');
  const [view, setView] = useState('list');
  const [form, setForm] = useState(() => getEmptyForm(getRoleOptions())); // 初始化
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All');
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const statusLabel = (status) => status === 1 ? 'Active' : 'Inactive';

  const filtered = users.filter(u => {
    const matchesSearch =
      (u.first_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.last_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'All' || statusLabel(u.status) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const fetchUsers = async (tenantRef) => {
    const role = localStorage.getItem("role");
    if (role !== "POSYB Admin" && !tenantRef) return;

    setIsLoading(true);
    try {
      let url = '/users/list';
      if (role === "POSYB Admin") {
        url = `/users/list`;
      } else if (tenantRef) {
        url = `/users/list?reference=${tenantRef}`;
      }
      const response = await axios.get(buildApiUrl(url), { headers: getAuthHeaders() });
      setUsers(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRestaurant?.reference) {
      fetchUsers(selectedRestaurant.reference);
    }else{
      fetchUsers();
    }
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageUsers = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalResults = filtered.length;
  const showingFrom = totalResults === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, totalResults);

  const handleSearch = (e) => { setSearch(e.target.value); setCurrentPage(1); };

  // 打开新增时，实时获取最新的 roleOptions
  const openAdd = () => {
    const role = localStorage.getItem("role");
    const restaurantSize = localStorage.getItem("RestaurantSize");
    if (role !== "POSYB Admin" && (!restaurantSize || Number(restaurantSize) <= 0)) {
      notify("Your account is not linked to any restaurants", 'error');
      return;
    }
    const currentOptions = getRoleOptions(); //  实时获取
    setForm({
      ...getEmptyForm(currentOptions),       //  用最新的
      role: currentOptions[0] || ''           //  默认选中第一个
    });
    setFormErrors({});
    setView('add');
  };

  // 编辑时也用实时的 roleOptions
  const openEdit = (user) => {
    const currentOptions = getRoleOptions();  //  实时获取
    setForm({
      reference: user.reference,
      username: user.username,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      contact: user.contact,
      password: '',
      role: user.role || currentOptions[0] || '',
      status: statusLabel(user.status),
      createdAt: user.createdAt
    });
    setFormErrors({});
    setView('edit');
  };

  const backToList = () => {
    setView('list');
    setForm(getEmptyForm(getRoleOptions()));  // 重置也用最新的
    setEditingId(null);
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const sanitized = name === 'contact' ? sanitizePhone(value) : value;
    setForm({ ...form, [name]: sanitized });
    setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateCreate = () => {
    const errors = {};
    if (!form.username) errors.username = 'Username is required.';
    if (!form.first_name) errors.first_name = 'First name is required.';
    if (!form.last_name) errors.last_name = 'Last name is required.';
    if (!form.email) errors.email = 'Email is required.';
    else if (!isValidEmail(form.email)) errors.email = 'Please enter a valid email address.';
    if (!form.contact) errors.contact = 'Contact is required.';
    if (!form.password) errors.password = 'Password is required.';
    if (!form.role) errors.role = 'Role is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateUpdate = () => {
    const errors = {};
    if (!form.first_name) errors.first_name = 'First name is required.';
    if (!form.last_name) errors.last_name = 'Last name is required.';
    if (!form.email) errors.email = 'Email is required.';
    else if (!isValidEmail(form.email)) errors.email = 'Please enter a valid email address.';
    if (!form.contact) errors.contact = 'Contact is required.';
    if (!form.role) errors.role = 'Role is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    const statusValue = form.status === 'Active' ? 1 : 0;
    try {
      const response = await fetch(buildApiUrl('/users/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          username: form.username,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          contact: form.contact,
          password: form.password,
          role: form.role,
          status: statusValue,
          reference: selectedRestaurant?.reference,
        }),
      });
      const data = await response.json();
      if (data.code === 200) {
        await fetchUsers(selectedRestaurant?.reference);
        backToList();
        notify(data.message || 'User was created successfully!', 'success');

      } else {
        notify(data.msg || 'Failed to create user.', 'error');
      }
    } catch (error) {
      notify('Error creating user: ' + error.message, 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateUpdate()) return;
    const statusValue = form.status === 'Active' ? 1 : 0;
    const body = {
      id: form.id,
      reference: form.reference,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      contact: form.contact,
      role: form.role,
      status: statusValue,
    };
    if (form.password) body.password = form.password;
    try {
      const response = await fetch(buildApiUrl('/users/update'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.code === 200) {
        await fetchUsers(selectedRestaurant?.reference);
        backToList();
        notify(data.message || 'User was updated successfully!', 'success');
      } else {
        notify(data.message || 'Failed to update user.', 'error');
      }
    } catch (error) {
      notify('Error updating user: ' + error.message, 'error');
    }
  };

  const [goToPage, setGoToPage] = React.useState('');

  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const renderUserManagement = () => {
    return (
      <>
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => onNavigate && onNavigate('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">User Management</span>
        </div>
        <h1 className="admin-page-title">User Management</h1>
        <p className="admin-page-subtitle">Manage all registered users, view their contact details and account status.</p>

        <HasPermi permission="users:list">
          <div className="user-list-card">
            <div className="user-list-header">
              <h2 className="user-list-title">User List</h2>
              <div className="user-list-actions">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search users..." value={search} onChange={handleSearch} />
                </div>
                <FaFilter className="filter-icon" />
                <select className="filter-select" value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <HasPermi permission="users:create">
                  <button className="add-user-btn" onClick={openAdd}><FiPlusCircle /> Add User</button>
                </HasPermi>
              </div>
            </div>

            <table className="user-table">
              <thead>
                <tr>
                  <th>USERNAME</th>
                  <th>CONTACT NAME</th>
                  <th>CONTACT INFO</th>
                  <th>ROLE</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageUsers.map((user, index) => (
                  <tr key={user.id ?? `temp-${index}`}>
                    <td>
                      <div className="username">{user.username}</div>
                      <RefId value={user.reference} />
                    </td>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>
                      <div className="contact-phone">{user.contact}</div>
                      <div className="contact-email">{user.email}</div>
                    </td>
                    <td>{user.role || '-'}</td>
                    <td>
                      <span className={`status-badge status-${statusLabel(user.status).toLowerCase()}`}>{statusLabel(user.status)}</span>
                    </td>
                    <td>{user.createdAt}</td>
                    <td>
                      <HasPermi permission="users:update">
                        <button className="edit-btn" onClick={() => openEdit(user)}><FaEdit /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {totalResults} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages}
                  value={goToPage} onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const p = parseInt(goToPage);
                      if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); }
                    }
                  }} placeholder="" />
                <button className="pg-btn" onClick={() => {
                  const p = parseInt(goToPage);
                  if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); }
                }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        <HasPermi permission="users:list"
          fallback={<div className="no-permission-message"><p>You don't have permission to view users. Please contact administrator.</p></div>}>
        </HasPermi>
      </>
    );
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="admin-root-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item}
              className={currentPageName === item ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>
      <main className="admin-main">
        {renderUserManagement()}
      </main>

      <HasPermi permission={['users:create', 'users:update']} any>
        {(view === 'add' || view === 'edit') && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={backToList}>×</button>
                <h2 className="admin-modal-title">{view === 'add' ? 'Create New User' : 'Edit User'}</h2>
                <p className="admin-modal-subtitle">
                  {view === 'add' ? 'Add a new user to the system. Fill in all required fields.' : 'Update the user information. Modify the fields as needed.'}
                </p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Username <span className="required">*</span></label>
                  <input className={formErrors.username ? 'input-error' : ''} name="username" value={form.username} onChange={handleFormChange} placeholder="Enter username" />
                  {formErrors.username && <p className="field-error">{formErrors.username}</p>}
                </div>
                <div className="form-field">
                  <label>First Name <span className="required">*</span></label>
                  <input className={formErrors.first_name ? 'input-error' : ''} name="first_name" value={form.first_name} onChange={handleFormChange} placeholder="Enter first name" />
                  {formErrors.first_name && <p className="field-error">{formErrors.first_name}</p>}
                </div>
                <div className="form-field">
                  <label>Last Name <span className="required">*</span></label>
                  <input className={formErrors.last_name ? 'input-error' : ''} name="last_name" value={form.last_name} onChange={handleFormChange} placeholder="Enter last name" />
                  {formErrors.last_name && <p className="field-error">{formErrors.last_name}</p>}
                </div>
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input className={formErrors.email ? 'input-error' : ''} name="email" value={form.email} onChange={handleFormChange} placeholder="Enter email address" />
                  {formErrors.email && <p className="field-error">{formErrors.email}</p>}
                </div>
                <div className="form-field">
                  <label>Contact <span className="required">*</span></label>
                  <input className={formErrors.contact ? 'input-error' : ''} name="contact" value={form.contact} onChange={handleFormChange} placeholder="e.g. +65 9123-4567" />
                  {formErrors.contact && <p className="field-error">{formErrors.contact}</p>}
                </div>
                <div className="form-field">
                  <label>Password {view === 'add' ? <span className="required">*</span> : <span className="optional">(Leave blank to keep current)</span>}</label>
                  <input className={formErrors.password ? 'input-error' : ''} name="password" type="password" value={form.password} onChange={handleFormChange} placeholder={view === 'add' ? 'Enter password' : 'Enter new password (optional)'} />
                  {formErrors.password && <p className="field-error">{formErrors.password}</p>}
                </div>
                <div className="form-field">
                  <label>Role <span className="required">*</span></label>
                  <select className={formErrors.role ? 'input-error' : ''} name="role" value={form.role} onChange={handleFormChange}>
                    {/*  使用组件内的 roleOptions */}
                    {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {formErrors.role && <p className="field-error">{formErrors.role}</p>}
                </div>
                <div className="form-field">
                  <label>Status <span className="required">*</span></label>
                  <select name="status" value={form.status} onChange={handleFormChange}>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={backToList}>Cancel</button>
                <button className="submit-btn" onClick={view === 'add' ? handleCreate : handleUpdate}>
                  {view === 'add' ? 'Create User' : 'Update User'}
                </button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default AdminPage;
